/**
 * LnRulesMatrixPage — Reference Data · LN Verification Rules.
 *
 * BACKLOG-0501 Task C admin editor. Consumes the existing admin endpoints
 * shipped in AdminLnVerificationRulesFunction.cs:
 *   GET   /v3/admin/ln-verification-rules     — all rules incl. inactive
 *   PATCH /v3/admin/ln-verification-rules/{id} — mode/note/citation/isActive
 *
 * Renders the matrix as a sortable table grouped on the natural-key tuple
 * (cohort × citizenship × tenure × channel). Mode is the editable cell;
 * Note + LegalCitation + IsActive live behind an Edit modal so the
 * grid stays compact and the citation discipline (every edit re-stamps a
 * citation) shows in the modal header.
 *
 * No DELETE / no CREATE today — the seeded matrix is 6 rows for Personal
 * cohort. Additional cohorts (Business / Trust / Estate) ship a seed-
 * correction migration first; this editor only updates existing rows.
 *
 * Auth: MSAL Path B via ADMIN_AUTH_TOKEN_SENTINEL (the same sentinel the
 * CD penalty / pilot officer editors use). The Function-side AdminAuthService
 * resolves to a Path B bearer (preferred) or Path C officer-code header.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type LnVerificationRule,
  type LnVerificationMode,
  type LnVerificationRulePatchBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextArea, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface EditDraft {
  id: number;
  cohort: string;
  citizenship: string;
  tenure: string;
  channel: string;
  mode: LnVerificationMode;
  note: string;
  legalCitation: string;
  isActive: boolean;
}

function rowToDraft(r: LnVerificationRule): EditDraft {
  return {
    id: r.id,
    cohort: r.cohort,
    citizenship: r.citizenship,
    tenure: r.tenure,
    channel: r.channel,
    mode: r.mode,
    note: r.note ?? '',
    legalCitation: r.legalCitation,
    isActive: r.isActive,
  };
}

function draftToPatch(original: LnVerificationRule, draft: EditDraft): LnVerificationRulePatchBody {
  // Only send fields the user actually changed. Avoids re-stamping audit
  // timestamps on no-op saves (server still stamps on any PATCH, but
  // sending only the deltas keeps the audit log truthful about intent).
  const body: LnVerificationRulePatchBody = {};
  if (draft.mode !== original.mode) body.mode = draft.mode;
  const normalizedNote = draft.note.trim().length === 0 ? null : draft.note.trim();
  if (normalizedNote !== (original.note ?? null)) body.note = normalizedNote;
  if (draft.legalCitation.trim() !== original.legalCitation.trim()) body.legalCitation = draft.legalCitation.trim();
  if (draft.isActive !== original.isActive) body.isActive = draft.isActive;
  return body;
}

function modeBadgeTone(mode: LnVerificationMode): 'ok' | 'warn' | 'err' | 'muted' {
  switch (mode) {
    case 'REQUIRED': return 'err';     // load-bearing — hard block on missing extract
    case 'PROHIBIT': return 'warn';    // intentional suppression — must NOT run LN
    case 'OPTIONAL': return 'ok';      // standard, no gate
    default: return 'muted';
  }
}

function formatTuple(r: LnVerificationRule): string {
  return `${r.cohort} · ${r.citizenship} · ${r.tenure} · ${r.channel}`;
}

export function LnRulesMatrixPage(): JSX.Element {
  const client = useApiClient();
  const [rules, setRules] = useState<ReadonlyArray<LnVerificationRule> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [original, setOriginal] = useState<LnVerificationRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRules(null);
      setLoadError(null);
      try {
        const resp = await client.retail.admin.lnVerificationRules.list();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRules(resp.data);
        } else {
          setLoadError(resp.error ?? 'Failed to load LN verification rules.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const sorted = useMemo(() => {
    if (!rules) return rules;
    return [...rules].sort((a, b) => {
      if (a.cohort !== b.cohort) return a.cohort.localeCompare(b.cohort);
      if (a.citizenship !== b.citizenship) return a.citizenship.localeCompare(b.citizenship);
      if (a.tenure !== b.tenure) return a.tenure.localeCompare(b.tenure);
      return a.channel.localeCompare(b.channel);
    });
  }, [rules]);

  const counts = useMemo(() => {
    const c = { required: 0, optional: 0, prohibit: 0, inactive: 0 };
    for (const r of rules ?? []) {
      if (!r.isActive) { c.inactive++; continue; }
      if (r.mode === 'REQUIRED') c.required++;
      else if (r.mode === 'OPTIONAL') c.optional++;
      else if (r.mode === 'PROHIBIT') c.prohibit++;
    }
    return c;
  }, [rules]);

  const columns: ReadonlyArray<DataTableColumn<LnVerificationRule>> = useMemo(
    () => [
      {
        header: 'Cohort',
        cell: (r) => <span>{r.cohort}</span>,
      },
      {
        header: 'Citizenship',
        cell: (r) => <span>{r.citizenship}</span>,
      },
      {
        header: 'Tenure',
        cell: (r) => <span>{r.tenure}</span>,
      },
      {
        header: 'Channel',
        cell: (r) => <span>{r.channel}</span>,
      },
      {
        header: 'Mode',
        cell: (r) => <Pill tone={modeBadgeTone(r.mode)}>{r.mode}</Pill>,
      },
      {
        header: 'Note',
        cell: (r) => (
          <span style={{ fontSize: 12, color: 'var(--ink-700, #4b5563)' }}>
            {r.note ?? <em style={{ opacity: 0.6 }}>(none)</em>}
          </span>
        ),
      },
      {
        header: 'Legal citation',
        cell: (r) => (
          <span
            style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)' }}
            title={r.legalCitation}
          >
            {r.legalCitation.length > 50 ? `${r.legalCitation.slice(0, 50)}…` : r.legalCitation}
          </span>
        ),
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: 'Last edit',
        cell: (r) => (
          <span style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)' }}>
            {r.lastUpdatedByOfficerCode ?? '—'}
            {r.lastUpdatedUtc && (
              <>
                {' · '}
                {new Date(r.lastUpdatedUtc).toLocaleDateString()}
              </>
            )}
          </span>
        ),
      },
      {
        header: '',
        cell: (r) => (
          <Button
            onClick={() => {
              setOriginal(r);
              setDraft(rowToDraft(r));
              setSaveError(null);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (!draft || !original) return;
    if (draft.legalCitation.trim().length === 0) {
      setSaveError('Legal citation is required — every edit must cite a procedure / PDF / confirmation.');
      return;
    }
    const patch = draftToPatch(original, draft);
    if (Object.keys(patch).length === 0) {
      setSaveError('No changes to save.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.lnVerificationRules.patch(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draft.id,
        patch,
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'patch returned success=false');
        return;
      }
      setDraft(null);
      setOriginal(null);
      setToast({
        tone: 'info',
        text: `Updated rule ${formatTuple(original)} — ${Object.keys(patch).join(', ')}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, original, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load LN verification rules: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rules) return <Spinner label="Loading LN verification rules…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Admin-toggleable LN-verification matrix. Drives the Verify-tab Run-LN
        button visibility in PersonModal (BACKLOG-0501 Task A) and the
        cart-commit preflight gate (Task B). REQUIRED rules block commit
        when the Person has no IdentityProviderExtract; PROHIBIT hides the
        Run-LN UI entirely; OPTIONAL is the default permissive mode.
        Inactive rows are ignored at runtime — the next-broader rule wins.
      </Banner>

      <Card
        title={
          <span>
            LN Verification Rules — {rules.length} total
            {' '}
            <Pill tone="err">REQUIRED {counts.required}</Pill>{' '}
            <Pill tone="ok">OPTIONAL {counts.optional}</Pill>{' '}
            <Pill tone="warn">PROHIBIT {counts.prohibit}</Pill>{' '}
            {counts.inactive > 0 && <Pill tone="muted">inactive {counts.inactive}</Pill>}
          </span>
        }
      >
        <DataTable<LnVerificationRule>
          columns={columns}
          rows={sorted ?? []}
          rowKey={(r) => String(r.id)}
          emptyState={<p className="empty">No LN verification rules seeded.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={original ? `Edit rule — ${formatTuple(original)}` : 'Edit rule'}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setOriginal(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button
                onClick={() => {
                  setDraft(null);
                  setOriginal(null);
                  setSaveError(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && original && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div
              style={{
                padding: 'var(--sp-3)',
                background: 'var(--surface-2, #f9fafb)',
                border: '1px solid var(--ink-200, #e5e7eb)',
                borderRadius: 4,
                fontSize: 12,
                color: 'var(--ink-700, #4b5563)',
              }}
            >
              <strong>Natural key</strong> (locked — re-key requires a new seed migration):
              <br />
              <code>
                {draft.cohort} / {draft.citizenship} / {draft.tenure} / {draft.channel}
              </code>
            </div>

            <SelectField
              label="Mode"
              value={draft.mode}
              onChange={(v) => setDraft({ ...draft, mode: v as LnVerificationMode })}
              options={[
                { value: 'REQUIRED', label: 'REQUIRED — block commit if no LN report' },
                { value: 'OPTIONAL', label: 'OPTIONAL — show Run-LN button, no gate' },
                { value: 'PROHIBIT', label: 'PROHIBIT — hide Run-LN button entirely' },
              ]}
              hint="REQUIRED gates cart-commit; PROHIBIT hides the LN UI; OPTIONAL allows but doesn't require."
            />

            <TextArea
              label="Note"
              value={draft.note}
              onChange={(v) => setDraft({ ...draft, note: v })}
              placeholder="FSR-visible context (e.g. 'address verification alternative — run LN only if on-file address differs from presented ID')."
              rows={3}
              hint="Surfaces in the PersonModal Run-LN button tooltip on REQUIRED rules."
            />

            <TextArea
              label="Legal citation"
              value={draft.legalCitation}
              onChange={(v) => setDraft({ ...draft, legalCitation: v })}
              placeholder="PCB procedure memo / Legacy Procedures PDF page / Aurora finding / Bill confirmation date."
              rows={2}
              hint="Required. Cited in audit logs + cart-commit block reasons. Per the no-hallucinated-defaults rule, every edit must trace to a source."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive rules are ignored at runtime — the next-broader rule wins. The row stays for audit + future re-activation."
            />

            <div style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)' }}>
              Last edit: {original.lastUpdatedByOfficerCode ?? '—'}
              {original.lastUpdatedUtc && (
                <>
                  {' · '}
                  {new Date(original.lastUpdatedUtc).toLocaleString()}
                </>
              )}
            </div>
          </div>
        )}
      </ModalShell>
    </>
  );
}
